import { useState, useCallback, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MessageCircle, Send, Reply, ChevronDown, ChevronUp, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { sfxComment } from "@/lib/sfx";
import { triggerPointsAnimation } from "@/components/PointsRewardAnimation";

export type ThreadComment = {
  id: string;
  content: string;
  created_at: string;
  display_name: string;
  avatar_url: string;
  parent_id: string | null;
  user_id?: string;
};

interface CommentLikeInfo {
  count: number;
  likedByMe: boolean;
  likedByCreator: boolean;
}

interface CommentThreadProps {
  postId: string;
  comments: ThreadComment[];
  setComments: React.Dispatch<React.SetStateAction<ThreadComment[]>>;
  compact?: boolean;
  postOwnerId?: string;
}

/** Shared threaded comment list + reply input used across feed modals */
const CommentThread = ({ postId, comments, setComments, compact = false, postOwnerId }: CommentThreadProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<{ id: string; name: string } | null>(null);
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const [commentLikes, setCommentLikes] = useState<Map<string, CommentLikeInfo>>(new Map());

  const topLevel = comments.filter((c) => !c.parent_id);
  const repliesMap = new Map<string, ThreadComment[]>();
  comments.forEach((c) => {
    if (c.parent_id) {
      const arr = repliesMap.get(c.parent_id) ?? [];
      arr.push(c);
      repliesMap.set(c.parent_id, arr);
    }
  });

  // Load comment likes
  useEffect(() => {
    if (comments.length === 0) return;
    const commentIds = comments.map((c) => c.id);
    (async () => {
      const { data } = await supabase
        .from("comment_likes")
        .select("comment_id, user_id")
        .in("comment_id", commentIds);
      if (!data) return;

      const map = new Map<string, CommentLikeInfo>();
      for (const id of commentIds) {
        map.set(id, { count: 0, likedByMe: false, likedByCreator: false });
      }
      for (const row of data) {
        const info = map.get(row.comment_id);
        if (!info) continue;
        info.count++;
        if (user && row.user_id === user.id) info.likedByMe = true;
        if (postOwnerId && row.user_id === postOwnerId) info.likedByCreator = true;
      }
      setCommentLikes(map);
    })();
  }, [comments, user, postOwnerId]);

  const toggleCommentLike = async (commentId: string) => {
    if (!user) { navigate("/auth"); return; }
    const info = commentLikes.get(commentId) ?? { count: 0, likedByMe: false, likedByCreator: false };

    if (info.likedByMe) {
      // Optimistic unlike
      setCommentLikes((prev) => {
        const next = new Map(prev);
        next.set(commentId, { ...info, count: Math.max(0, info.count - 1), likedByMe: false, likedByCreator: postOwnerId === user.id ? false : info.likedByCreator });
        return next;
      });
      await supabase.from("comment_likes").delete().eq("comment_id", commentId).eq("user_id", user.id);
    } else {
      // Optimistic like
      setCommentLikes((prev) => {
        const next = new Map(prev);
        next.set(commentId, { ...info, count: info.count + 1, likedByMe: true, likedByCreator: postOwnerId === user.id ? true : info.likedByCreator });
        return next;
      });
      await supabase.from("comment_likes").insert({ comment_id: commentId, user_id: user.id } as any);
    }
  };

  const handleSend = async () => {
    if (!user) { navigate("/auth"); return; }
    if (!newComment.trim()) return;
    const content = newComment.trim();
    const parentId = replyingTo?.id ?? null;
    setNewComment("");
    setReplyingTo(null);
    sfxComment();
    triggerPointsAnimation(2);

    const { data } = await supabase
      .from("post_comments")
      .insert({ post_id: postId, user_id: user.id, content, parent_id: parentId } as any)
      .select("id, created_at")
      .single();

    const newC: ThreadComment = {
      id: data?.id ?? Date.now().toString(),
      content,
      created_at: data?.created_at ?? new Date().toISOString(),
      display_name: "You",
      avatar_url: "",
      parent_id: parentId,
      user_id: user.id,
    };
    setComments((prev) => [...prev, newC]);

    if (parentId) {
      setExpandedReplies((prev) => new Set(prev).add(parentId));
    }
  };

  const toggleReplies = (commentId: string) => {
    setExpandedReplies((prev) => {
      const next = new Set(prev);
      next.has(commentId) ? next.delete(commentId) : next.add(commentId);
      return next;
    });
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  const renderComment = (c: ThreadComment, isReply = false) => {
    const likeInfo = commentLikes.get(c.id);
    return (
      <motion.div
        key={c.id}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-start gap-2 ${isReply ? "ml-8 mt-2" : ""}`}
      >
        <Avatar className={`${compact ? "h-6 w-6" : "h-7 w-7"} flex-shrink-0`}>
          {c.avatar_url && <AvatarImage src={c.avatar_url} />}
          <AvatarFallback className="text-[8px] font-display bg-secondary text-foreground">
            {c.display_name.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="text-xs">
            <span className="font-display font-bold text-foreground">{c.display_name}</span>
            <span className="text-muted-foreground ml-1.5">{timeAgo(c.created_at)}</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 break-words">{c.content}</p>
          <div className="flex items-center gap-3 mt-1">
            {/* Like button */}
            <button
              onClick={() => toggleCommentLike(c.id)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors"
            >
              <Heart className={`h-3 w-3 transition-all ${likeInfo?.likedByMe ? "fill-primary text-primary" : ""}`} />
              {(likeInfo?.count ?? 0) > 0 && <span>{likeInfo!.count}</span>}
            </button>

            {/* Creator liked badge */}
            {likeInfo?.likedByCreator && (
              <span className="flex items-center gap-0.5 text-[10px] text-primary font-display font-semibold">
                <Heart className="h-2.5 w-2.5 fill-primary text-primary" />
                Creator
              </span>
            )}

            {!isReply && (
              <button
                onClick={() => setReplyingTo({ id: c.id, name: c.display_name })}
                className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors"
              >
                <Reply className="h-3 w-3" />
                Reply
              </button>
            )}
            {!isReply && (repliesMap.get(c.id)?.length ?? 0) > 0 && (
              <button
                onClick={() => toggleReplies(c.id)}
                className="flex items-center gap-1 text-[10px] text-primary/80 hover:text-primary transition-colors"
              >
                {expandedReplies.has(c.id) ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                {repliesMap.get(c.id)!.length} {repliesMap.get(c.id)!.length === 1 ? "reply" : "replies"}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <div className={`${compact ? "p-2" : "p-3"} space-y-3 overflow-y-auto`} style={{ maxHeight: compact ? "50vh" : "40vh" }}>
        {topLevel.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <MessageCircle className="h-6 w-6 text-muted-foreground/30 mb-1.5" />
            <p className="text-xs text-muted-foreground">No comments yet — be the first!</p>
          </div>
        ) : (
          topLevel.map((c) => (
            <div key={c.id}>
              {renderComment(c)}
              <AnimatePresence>
                {expandedReplies.has(c.id) &&
                  repliesMap.get(c.id)?.map((r) => renderComment(r, true))}
              </AnimatePresence>
            </div>
          ))
        )}
      </div>

      {/* Input */}
      <div className={`${compact ? "p-2" : "p-2.5"} border-t border-border/50`}>
        <AnimatePresence>
          {replyingTo && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="flex items-center justify-between px-3 py-1 mb-1 rounded-lg bg-primary/10 text-xs text-primary">
                <span className="flex items-center gap-1">
                  <Reply className="h-3 w-3" />
                  Replying to <strong>{replyingTo.name}</strong>
                </span>
                <button onClick={() => setReplyingTo(null)} className="text-muted-foreground hover:text-foreground">✕</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="flex items-center gap-2 rounded-full bg-secondary/50 px-3 py-1">
          <Input
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder={replyingTo ? `Reply to ${replyingTo.name}...` : "Add a comment..."}
            className="h-8 min-w-0 flex-1 text-xs bg-transparent border-0 focus-visible:ring-0 px-0 placeholder:text-muted-foreground text-foreground"
          />
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-primary hover:text-primary shrink-0"
            onClick={handleSend}
            disabled={!newComment.trim()}
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </>
  );
};

export default CommentThread;

/** Utility to load threaded comments for a post */
export async function loadThreadedComments(postId: string): Promise<ThreadComment[]> {
  const { data } = await supabase
    .from("post_comments")
    .select("id, content, created_at, user_id, parent_id")
    .eq("post_id", postId)
    .order("created_at", { ascending: true })
    .limit(100);

  if (!data) return [];

  const userIds = [...new Set(data.map((c) => c.user_id))];
  const { data: profiles } = await supabase
    .from("profiles")
    .select("user_id, display_name, avatar_url")
    .in("user_id", userIds.length > 0 ? userIds : ["none"]);

  const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) ?? []);

  return data.map((c) => ({
    id: c.id,
    content: c.content,
    created_at: c.created_at,
    display_name: profileMap.get(c.user_id)?.display_name || "Fan",
    avatar_url: profileMap.get(c.user_id)?.avatar_url || "",
    parent_id: (c as any).parent_id ?? null,
    user_id: c.user_id,
  }));
}
